import React from 'react';
import { TFunction } from 'i18next';
import useBundleTranslation from 'i18n';
import { Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import { add, remove } from 'store/popupSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Breakpoint,
    Typography,
    CircularProgress,
} from '@mui/material';

import IconMi from 'components/common/icon/IconMi';
import { SxProps } from '@mui/system';

export type PopupSettings = {
    title?: string;
    titleConfig?: {
        icon: string;
    };
    subTitle?: string;
    subTitleConfig?: {
        icon: string;
        type: string;
        text: string;
        color: string;
    };
    customSubTitle?: React.JSX.Element;
    textOK?: string;
    colorOk?: string;
    iconOK?: string;
    textCancel?: string;
    needTranslation?: boolean;
    noCancel?: boolean;
    noOk?: boolean;
    noButtons?: boolean;
    maxWidth?: string | false;
    customMaxWidth?: string;
    asidePanel?: boolean;
    customActionPanel?: React.JSX.Element;
    uid?: string;
    actionsPostContent?: React.JSX.Element;
};

export function Popup({
    children,
    settings,
    open,
    onConfirm,
    onHide,
    onBackdropClick = () => {},
    okDisabled = false,
    isLoading = false,
    maxWidth = 'popupLg',
    fullWidth = true,
    scrollType = 'paper',
    additionalButtons,
    actionsPreContent,
    keepMounted = false,
    sx,
    translationNS,
    t,
    className,
    onCancel,
    onPopupKeyDownEnter,
    fullScreen = false,
}: {
    isLoading?: boolean;
    children: any;
    settings: PopupSettings;
    open: boolean;
    onConfirm?: any;
    onHide: any;
    onBackdropClick?: () => void;
    okDisabled?: boolean;
    maxWidth?: Breakpoint | false;
    fullWidth?: boolean;
    scrollType?: 'body' | 'paper';
    additionalButtons?: Array<React.JSX.Element>;
    actionsPreContent?: any;
    keepMounted?: boolean;
    sx?: SxProps<Theme>;
    translationNS?: string;
    t?: TFunction;
    className?: string;
    onCancel?: () => void;
    onPopupKeyDownEnter?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    fullScreen?: boolean;
}) {
    if (undefined === t) t = useBundleTranslation([translationNS ?? 'components/common/grid/grid'])['t'];

    onConfirm = onConfirm ?? onHide;

    const dispatch = useDispatch();

    const currentPopupIndex = useSelector((state: any) => state.popup.popupCounter);

    const [popupIndex] = useState(currentPopupIndex);
    const [modelClass, setModelClass] = useState('');
    useEffect(() => {
        setModelClass(popupIndex + 1 == currentPopupIndex ? '' : 'd-none');
    }, [currentPopupIndex]);

    useEffect(() => {
        dispatch(add());
        return () => {
            dispatch(remove());
        };
    }, []);

    const getPanelProps = () => {
        if (settings.asidePanel) {
            return {
                className: 'popup-component__container',
                sx: {
                    margin: '40px 0 0 auto !important',
                    maxHeight: 'calc(100% - 40px) !important',
                    width: '600px',
                    height: '100%',
                },
            };
        }
        return { className: 'popup-component__container' };
    };

    const getContentWrapperSx: any = () => {
        if (settings.asidePanel) {
            return {
                padding: 0,
                '.pinned-components': { px: 3 },
                '.default-components': { px: 3, py: 3 },
            };
        }

        if (scrollType === 'body') return { overflow: 'visible' };
        if (scrollType === 'paper') return { overflowX: 'hidden' };
        return {};
    };

    const buildCustomSubTittle = () => {
        if (settings.subTitleConfig) {
            const { color, icon, text } = settings.subTitleConfig;

            const firstPartIndex = text?.indexOf('<<');
            const secondPartIndex = text?.indexOf('>>');

            const firstPart = text?.substring(0, firstPartIndex).trim();
            const iconText = text.substring(firstPartIndex + 2, secondPartIndex);
            const lastPart = text?.substring(secondPartIndex + 2).trim();
            const iconElement = (
                <Typography color={color} sx={{ display: 'flex' }}>
                    <IconMi icon={icon} fontSize={'16'} sx={{ marginRight: '2px' }} />
                    {iconText}
                </Typography>
            );

            return (
                <Typography className="popup-subtitle" sx={{ pt: '2px', display: 'flex' }}>
                    {firstPart}
                    &nbsp;
                    {iconElement}
                    &nbsp;
                    {lastPart}
                </Typography>
            );
        }
        return null;
    };

    const dataTestAttr = 'popup_' + (settings.uid ?? settings.title);

    const actionsPostContent = settings.actionsPostContent ?? null;

    const popupMaxWidthSetting = (undefined !== settings?.maxWidth ? settings.maxWidth : maxWidth) as
        | Breakpoint
        | false;

    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth={fullWidth}
            maxWidth={popupMaxWidthSetting}
            open={open}
            disableEscapeKeyDown={!onHide}
            onClose={(event, reason) => {
                if (reason && reason == 'backdropClick') {
                    onBackdropClick();
                } else {
                    onHide(event);
                }
            }}
            scroll={scrollType}
            sx={{
                ...{
                    '.MuiDialog-paper': {
                        overflow: 'visible',
                        marginTop: fullScreen ? '0' : '100px',
                        alignSelf: 'flex-start',
                    },
                    '.MuiDialog-container:after': {
                        content: 'none',
                    },
                    '.MuiDialog-paperScrollPaper': { maxHeight: fullScreen ? 'none' : 'calc(100% - 132px)' },
                },
                ...sx,
                ...{ '.MuiDialog-container .MuiDialog-paper': { maxWidth: settings.customMaxWidth ?? undefined } },
            }}
            keepMounted={keepMounted}
            PaperProps={getPanelProps()}
            data-test={dataTestAttr + '_wrapper'}
            className={'popup-component' + (className ? ' ' + className : '')}
            onKeyDown={(event) => {
                const targetTypeEl = (event.target as any).type;
                if (event.key === 'Enter' && !['textarea'].includes(targetTypeEl)) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (onPopupKeyDownEnter) {
                        onPopupKeyDownEnter(event);
                    } else {
                        onConfirm();
                    }
                }
            }}
        >
            <DialogTitle
                id="alert-dialog-title"
                sx={{
                    position: 'relative',
                    py: '10px',
                    minHeight: '48px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    className={'popup__title'}
                    sx={{
                        color: 'primary.main',
                        fontSize: '14px',
                        lineHeight: '18px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {settings.titleConfig?.icon && (
                        <IconMi icon={settings.titleConfig?.icon} sx={{ marginRight: '4px', fontSize: 18 }} />
                    )}
                    {false !== settings.needTranslation ? t(settings.title ?? 'info') : (settings.title ?? t('info'))}
                </Typography>
                {settings.subTitle && (
                    <Typography className="popup-subtitle" sx={{ pt: '2px' }}>
                        {false !== settings.needTranslation ? t(settings.subTitle) : settings.subTitle}
                    </Typography>
                )}
                {settings.subTitleConfig && buildCustomSubTittle()}
                {onHide ? (
                    <IconButton
                        aria-label="close"
                        onClick={onHide}
                        sx={{
                            position: 'absolute',
                            right: '16px',
                            top: '50%',
                            color: 'primary.main',
                            transform: 'translateY(-50%)',
                        }}
                    >
                        <IconMi icon="times" fontSize="12" />
                    </IconButton>
                ) : null}
            </DialogTitle>
            <DialogContent dividers sx={getContentWrapperSx()} data-test={dataTestAttr}>
                {children}
            </DialogContent>
            {settings.noButtons !== true && !settings.customActionPanel && (
                <DialogActions sx={{ justifyContent: 'normal', py: 2, px: 3, flexWrap: 'wrap', display: 'flex' }}>
                    {actionsPreContent}
                    {additionalButtons && additionalButtons.map((button) => button)}
                    {!settings!.noOk && (
                        <Button
                            variant={additionalButtons ? 'outlined' : 'contained'}
                            onClick={onConfirm}
                            disabled={okDisabled}
                            color={settings.colorOk as any}
                            className={'popup-button--ok'}
                            data-test={dataTestAttr + '_ok_button'}
                            startIcon={settings.iconOK ? <IconMi icon={settings.iconOK} /> : undefined}
                        >
                            {isLoading && (
                                <CircularProgress
                                    sx={{ marginRight: '5px' }}
                                    color={additionalButtons ? 'primary' : 'inherit'}
                                    size={12}
                                />
                            )}

                            {settings.textOK
                                ? false !== settings.needTranslation
                                    ? t(settings.textOK)
                                    : settings.textOK
                                : t('save')}
                        </Button>
                    )}

                    {!settings!.noCancel && (
                        <Button
                            data-test={dataTestAttr + '_cancel_button'}
                            variant="outlined"
                            color="neutral"
                            onClick={onCancel ? onCancel : onHide}
                            className={'popup-button--cancel'}
                        >
                            {settings.textCancel
                                ? false !== settings.needTranslation
                                    ? t(settings.textCancel)
                                    : settings.textCancel
                                : t('cancel')}
                        </Button>
                    )}
                    {actionsPostContent}
                </DialogActions>
            )}
            {settings.customActionPanel}
        </Dialog>
    );
}
